import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Card, Button, Space, message, Popconfirm, Row, Col, Input, Select, Drawer, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import PageLayout from '../../components/layout/Layout';
import company from '../../services/company';
import './index.css';
import env from '../../env';
import { auditColumns, formatDate } from '../../utils/audit';

const { Search } = Input;
const { Title } = Typography;

const CompaniesPage = () => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [filterLob, setFilterLob] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0
  });

  const navigate = useNavigate();

  // Replace fetchCompanies with useQuery
  const { data, isLoading, error } = useQuery({
    queryKey: ['companies', pagination.current, pagination.pageSize, searchText],
    queryFn: async () => {
      const searchParam = searchText ? `&query=${encodeURIComponent(searchText.trim())}` : '';
      const response = await company.get(`?page=${pagination.current}&per_page=${pagination.pageSize}${searchParam}`);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: response.data.meta.per_page,
        total: response.data.meta.total,
      });
      return response.data;
    },
    cacheTime: 1000 * 60 * 5,  // Cache for 5 minutes
    staleTime: 1000 * 60 * 2,  // Fresh for 2 minutes
    refetchOnWindowFocus: true, // Refetch when window is focused
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => company.delete(id),
    onSuccess: () => {
      message.success('Company deleted successfully');
      queryClient.invalidateQueries(['companies']);
    },
    onError: (error) => {
      console.error('Delete error:', error);
      message.error(error.response?.data?.message || 'Failed to delete company');
    },
  });

  const handleDelete = async (id) => {
    console.log('Attempting to delete company with ID:', id);
    deleteMutation.mutate(id);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const onDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedCompany(null);
  };

  const columns = [
    {
      title: '-',
      dataIndex: 'image',
      key: 'image',
      width: 60,
      render: (text, record) => (
        <div className="company-avatar">
          {record.logo ? (
            <img
              src={`${env.BACKEND_STORAGE_URL}/${record.logo}`}
              width={40}
              height={40}
              style={{ borderRadius: '50%' }}
            />
          ) : null}
        </div>
      )
    },
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text) => <span>{text}</span>
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    ...auditColumns,
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="middle" className="action-buttons">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedCompany(record);
              setDrawerVisible(true);
            }}
            className="view-button"
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/company/${record.slug}/edit`)}
            className="edit-button"
            title="Edit Company"
          />
          <Popconfirm
            title="Delete Company"
            description="Are you sure you want to delete this company?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              className="delete-button"
              title="Delete Company"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleFilterChange = (value) => {
    setFilterLob(value);
    
    // Filter companies based on current search text and new LOB filter
    const filteredResults = data?.data.filter(company => {
      const matchesSearch = searchText ? (
        (company.name?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (company.contact?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (company.phone || '').includes(searchText) ||
        (company.website?.toLowerCase() || '').includes(searchText.toLowerCase())
      ) : true;

      const matchesFilter = value === 'all' || company.line_of_business === value;
      
      return matchesSearch && matchesFilter;
    });

    setPagination(prev => ({
      ...prev,
      current: 1,
      total: filteredResults.length
    }));
  };

  // const styles = `
  //   .ant-table-cell .ant-tag {
  //     margin: 2px;
  //   }
    
  //   .ant-table-cell .ant-space {
  //     flex-wrap: wrap;
  //   }
    
  //   .ant-tag-blue {
  //     background: #e6f7ff;
  //     border-color: #91d5ff;
  //     color: #1890ff;
  //   }
    
  //   .ant-tag-green {
  //     background: #f6ffed;
  //     border-color: #b7eb8f;
  //     color: #52c41a;
  //   }
  // `;

  // Update the drawer content
  const DrawerContent = ({ company }) => (
    <div className="company-details">
      <div className="company-header">
        {company.logo && (
          <img 
            src={`${env.BACKEND_STORAGE_URL}/${company.logo}`} 
            alt={company.name}
            className="company-logo" 
          />
        )}
        <h2>{company.name}</h2>
      </div>

      <div className="detail-sections">
        <div className="detail-section">
          <h3>States</h3>
          <div className="tags-container">
            {company.states?.map((state) => (
              <Tag color="blue" key={state.id}>
                {state.name}
              </Tag>
            ))}
          </div>
        </div>

        <div className="detail-section">
          <h3>Line of Business</h3>
          <div className="tags-container">
            {company.line_of_businesses?.map((lob) => (
              <Tag color="green" key={lob.id}>
                {lob.name}
              </Tag>
            ))}
          </div>
        </div>

        <div className="detail-section">
          <h3>Contact Information</h3>
          <div className="info-grid">
            {company.contact && (
              <div className="info-item">
                <strong>Contact Person:</strong>
                <span>{company.contact}</span>
              </div>
            )}
            {company.phone && (
              <div className="info-item">
                <strong>Phone:</strong>
                <span>{company.phone}</span>
              </div>
            )}
            {company.website && (
              <div className="info-item">
                <strong>Website:</strong>
                <a href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              </div>
            )}
            <div className="info-item">
              <strong>Added by:</strong>
              <span>{company.created_by?.name || '—'}</span>
            </div>
            <div className="info-item">
              <strong>Updated by:</strong>
              <span>{company.updated_by?.name || '—'}</span>
            </div>
            <div className="info-item">
              <strong>Last updated:</strong>
              <span>{formatDate(company.updated_at)}</span>
            </div>
          </div>
        </div>

        {/* {company.general_info && (
          <div className="detail-section">
            <h3>About</h3>
            <p>{company.general_info}</p>
          </div>
        )} */}

        {company.files && company.files.length > 0 && (
          <div className="detail-section">
            <h3>Documents</h3>
            <div className="documents-list">
              {company.files.map((file, index) => (
                <a 
                  key={index}
                  href={`${env.BACKEND_STORAGE_URL}/${file.file_path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="document-link"
                >
                  {file.file_name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (error) {
    message.error('Failed to fetch companies');
  }

  return (
    <PageLayout>
      <div className="companies-container">
        <div className="page-header">
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2}>Companies</Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/admin/company/add')}
              >
                Add Company
              </Button>
            </Col>
          </Row>
        </div>

        <Card>
          <Row gutter={[16, 16]} >
            <Col xs={24} md={12} lg={8}>
              <Search
                placeholder="Search companies..."
                onSearch={handleSearch}
                allowClear
                size="large"
                loading={isLoading}
              />
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={data?.data || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              total: pagination.total,
              pageSize: pagination.pageSize,
              current: pagination.current,
            }}
            onChange={handleTableChange}
            scroll={{ x: true }}
            rowClassName={(record) => record.sponsored === "1" ? 'sponsored-row' : ''}
          />
        </Card>
      </div>
      <Drawer
        title="Company Details"
        placement={window.innerWidth <= 768 ? "bottom" : "right"}
        onClose={onDrawerClose}
        open={drawerVisible}
        width={window.innerWidth <= 768 ? '100%' : 520}
        height={window.innerWidth <= 768 ? '90vh' : undefined}
        className="company-drawer"
      >
        {selectedCompany && <DrawerContent company={selectedCompany} />}
      </Drawer>
    </PageLayout>
  );
};

export default CompaniesPage;
